import { Table, Modal, Input } from "antd";
import { useState } from "react";
import { toast } from "react-toastify";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Head from "../../Components/Head";
import { useCreateTour, useGetToursList } from "../../api/tours";
import { useColumns } from "./useColumns";
import {
  getApiErrorMessage,
  getFieldErrors,
  getTourEditorPath,
} from "./utils/tourEditorUtils";
import styleTours from "./Tours.module.css";

const Tours = () => {
  const [tourName, setTourName] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTourName, setNewTourName] = useState("");
  const [titleError, setTitleError] = useState("");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [filters, setFilters] = useState({ title: "" });

  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetToursList({ limit, offset, ...filters });
  const { mutateAsync: createTour, isLoading: isCreating } = useCreateTour();
  const { columns } = useColumns(refetch);

  const handleSearch = () => {
    setOffset(0);
    setFilters({ title: tourName });
  };

  const handleAddTour = () => {
    setTitleError("");
    setIsModalOpen(true);
  };

  const openEditor = (title, id) => {
    window.open(
      `${window.location.origin}${getTourEditorPath(title, id)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleModalSubmit = async () => {
    const title = newTourName.trim();
    if (!title) {
      setTitleError("This field is required.");
      return;
    }

    setTitleError("");
    try {
      const res = await createTour({ title });
      const created = res?.data?.data;
      if (!created?.id) {
        toast.error("360 Tour Created, but no tour id was returned.");
        return;
      }
      openEditor(created.title || title, created.id);
      setIsModalOpen(false);
      setNewTourName("");
      refetch();
    } catch (err) {
      const fields = getFieldErrors(err);
      if (fields.title) setTitleError(fields.title);
      toast.error(getApiErrorMessage(err, "Failed to create tour"));
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setNewTourName("");
    setTitleError("");
  };

  const onChange = pagination => {
    setLimit(pagination?.pageSize);
    setOffset((pagination?.current - 1) * pagination?.pageSize);
  };

  const dataWithKeys = data?.data?.map((tour, index) => ({
    ...tour,
    key: tour.id,
    sn: offset + (index + 1),
  }));

  return (
    <main className={styleTours["tours-main-container"]}>
      <Head title="360 Tours" />
      <div className={styleTours["tours-form"]}>
        <div className={styleTours["tours-input-go-btn-container"]}>
          <input
            type="text"
            placeholder="Search by Tour Name"
            value={tourName}
            onChange={event => setTourName(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") handleSearch();
            }}
          />
          <button type="button" onClick={handleSearch}>
            Go
          </button>
        </div>
        <div className={styleTours["add-tour"]}>
          <button type="button" onClick={handleAddTour}>
            <FontAwesomeIcon icon={faPlus} /> Add Tour
          </button>
        </div>
      </div>
      {isError && <p>{getApiErrorMessage(error, error?.message)}</p>}
      <div className={styleTours["tours-table-container"]}>
        <Table
          rowClassName="cursor-pointer"
          columns={columns}
          dataSource={dataWithKeys}
          onChange={onChange}
          loading={isLoading || isFetching}
          pagination={{ total: data?.total, showSizeChanger: true }}
        />
      </div>
      <Modal
        title="Create New Tour"
        open={isModalOpen}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        okText="Create"
        confirmLoading={isCreating}
        okButtonProps={{ disabled: !newTourName.trim() || isCreating }}
      >
        <Input
          value={newTourName}
          status={titleError ? "error" : undefined}
          onChange={e => {
            setNewTourName(e.target.value);
            if (titleError) setTitleError("");
          }}
          placeholder="Enter Tour Name"
          onPressEnter={handleModalSubmit}
          autoFocus
        />
        {titleError && (
          <div style={{ color: "#ff4d4f", marginTop: 8 }}>{titleError}</div>
        )}
      </Modal>
    </main>
  );
};

export default Tours;
